import type { ReactNode } from 'react';
import { colour, radius, space, text } from '../design/tokens.ts';
import { Button, Card } from './basics.tsx';
import { ApiError } from '../api/client.ts';

/**
 * Screen 18: what people see when things are not normal.
 *
 * The wording is the mockup's, not mine. "The server did not answer. Your work
 * is safe." is doing a job that "Error" does not, and a screen that invents its
 * own sentence for the same situation makes the app feel like several apps.
 *
 * The distinction that matters is between **could not load** and **cannot
 * reach**. A failure means try again; being offline means keep showing what was
 * true and turn off the buttons that start work, because launching a run into a
 * connection that is not there is worse than waiting.
 */

// ── Loading ────────────────────────────────────────────────────────────────

/** A grey block standing in for something that is on its way. */
export function SkeletonBar({ width = '100%', height = 16 }: { width?: number | string | undefined; height?: number | undefined }) {
  return (
    <div
      style={{
        width,
        height,
        background: colour.edgeSoft,
        borderRadius: radius.item,
        animation: 'maestro-pulse 1.4s ease-in-out infinite'
      }}
    />
  );
}

export function Loading({ says, rows = 6 }: { says: string; rows?: number | undefined }) {
  return (
    <Card>
      <div
        role="status"
        aria-live="polite"
        style={{ display: 'flex', flexDirection: 'column', gap: space.control }}
      >
        <span style={{ ...text.bodyStrong, color: colour.inkSoft }}>{says}</span>
        {Array.from({ length: rows }, (_, i) => (
          <SkeletonBar key={i} width={`${100 - i * 6}%`} />
        ))}
      </div>
    </Card>
  );
}

// ── Nothing to show ────────────────────────────────────────────────────────

/** An empty screen: nothing has gone wrong, there is just nothing yet. */
export function Empty({
  title,
  says,
  action
}: {
  title: string;
  says: string;
  action?: ReactNode | undefined;
}) {
  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: space.control,
          padding: '48px 20px',
          textAlign: 'center'
        }}
      >
        <h2 style={{ margin: 0, ...text.h2 }}>{title}</h2>
        <span style={{ ...text.body, color: colour.inkSoft, maxWidth: 520 }}>{says}</span>
        {action}
      </div>
    </Card>
  );
}

/**
 * Filters that match nothing. Different from empty on purpose: the fix is to
 * change the filters, and the message says which ones are fighting.
 */
export function NoResults({ chips, onClear }: { chips: string[]; onClear: () => void }) {
  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: space.control,
          padding: '40px 20px',
          textAlign: 'center'
        }}
      >
        <h2 style={{ margin: 0, ...text.h2 }}>No results for these filters</h2>
        <span style={{ ...text.body, color: colour.inkSoft, maxWidth: 560 }}>
          {chips.length > 0
            ? `${chips.join(' and ')} together match nothing.`
            : 'Nothing matches what you have asked for.'}
        </span>
        <Button onClick={onClear}>Clear all filters</Button>
      </div>
    </Card>
  );
}

// ── Something went wrong ───────────────────────────────────────────────────

export function CouldNotLoad({
  what,
  error,
  onRetry
}: {
  /** "the project board", "this take" — what did not load. */
  what: string;
  error: unknown;
  onRetry: () => void;
}) {
  const api = error instanceof ApiError ? error : null;
  const when = new Date().toLocaleTimeString();
  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: space.control,
          padding: '40px 20px',
          textAlign: 'center'
        }}
      >
        <h2 style={{ margin: 0, ...text.h2 }}>Could not load {what}</h2>
        <span style={{ ...text.body, color: colour.inkSoft, maxWidth: 560 }}>
          {api?.message ?? 'The server did not answer. Your work is safe. Try again in a moment.'}
        </span>
        <Button tone="primary" onClick={onRetry}>
          Retry
        </Button>
        <span style={{ ...text.mono, color: colour.inkFaint }}>
          {api ? `Error code ${api.status}` : 'Error'} · {when}
        </span>
      </div>
    </Card>
  );
}

/** No permission: a page this role cannot reach at all. */
export function NoPermission({ what, role }: { what: string; role?: string | undefined }) {
  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: space.control,
          padding: '40px 20px',
          textAlign: 'center'
        }}
      >
        <h2 style={{ margin: 0, ...text.h2 }}>{what} is not open to your role</h2>
        <span style={{ ...text.body, color: colour.inkSoft, maxWidth: 520 }}>
          {role === 'viewer'
            ? 'Ask an admin to make you an operator. You can still look at everything.'
            : 'Ask an admin if you need to get in here.'}
        </span>
      </div>
    </Card>
  );
}

// ── Banners that sit above a screen ────────────────────────────────────────

function Banner({
  tone,
  title,
  says,
  action
}: {
  tone: 'quiet' | 'warn';
  title: string;
  says: ReactNode;
  action?: ReactNode | undefined;
}) {
  const warn = tone === 'warn';
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: space.control,
        padding: '12px 16px',
        background: warn ? colour.staleWash : colour.surfaceSunk,
        border: `1px solid ${warn ? colour.stale : colour.edge}`,
        borderRadius: radius.card
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ ...text.bodyStrong, color: warn ? colour.staleInk : colour.ink }}>{title}</span>
        <span style={{ ...text.small, color: colour.inkSoft }}>{says}</span>
      </span>
      {action}
    </div>
  );
}

/** Shown on every screen to a viewer, so the missing buttons are explained. */
export function ViewerBanner() {
  return (
    <Banner
      tone="quiet"
      title="You are a viewer"
      says="Read only. Launch and mark-good buttons are hidden for your role."
    />
  );
}

/**
 * The connection is gone. The screen keeps the data it had, and anything that
 * would start work is turned off until it is back (screen 18).
 */
export function OfflineBanner({ since, onRetry }: { since: Date; onRetry: () => void }) {
  return (
    <Banner
      tone="warn"
      title="Connection lost"
      says={`Offline. Showing what was true at ${since.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. Buttons that launch work are off until you are back.`}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}

/** A machine is off with work waiting on it, and the work can be moved. */
export function MachineOfflineBanner({
  host,
  waiting,
  onMove
}: {
  host: string;
  waiting: number;
  onMove?: () => void | undefined;
}) {
  return (
    <Banner
      tone="warn"
      title={`${host} is offline`}
      says={`${waiting === 1 ? '1 run is' : `${waiting} runs are`} waiting on it. They will start when it is back. You can move them to another machine that can run the same process.`}
      {...(onMove ? { action: <Button onClick={onMove}>Move the queued runs</Button> } : {})}
    />
  );
}

// ── Signed out ─────────────────────────────────────────────────────────────

export function SignedOut({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'grid',
        placeItems: 'center',
        padding: '40px 16px'
      }}
    >
      <Card style={{ maxWidth: 460, textAlign: 'center' }}>
        <h2 style={{ margin: 0, ...text.h2 }}>You were signed out</h2>
        <span style={{ ...text.body, color: colour.inkSoft }}>
          Nothing was lost. Anything you were typing is kept until you sign in again.
        </span>
        <Button tone="primary" onClick={onSignIn} style={{ alignSelf: 'center' }}>
          Sign in
        </Button>
      </Card>
    </div>
  );
}

/** A toast with an Undo, for something that just happened (screen 18). */
export function DoneToast({
  says,
  onUndo,
  onClose
}: {
  says: string;
  onUndo?: () => void | undefined;
  onClose: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        gap: space.control,
        padding: '12px 16px',
        background: colour.barBg,
        color: colour.barInk,
        borderRadius: radius.card,
        boxShadow: '0 12px 32px rgba(27, 28, 30, 0.32)',
        maxWidth: 'min(92vw, 560px)'
      }}
    >
      <span style={text.small}>{says}</span>
      {onUndo && (
        <button
          type="button"
          onClick={onUndo}
          style={{
            minHeight: 36,
            padding: '0 12px',
            border: `1px solid ${colour.barEdge}`,
            borderRadius: radius.control,
            background: colour.barRaised,
            color: colour.barInk,
            font: text.smallStrong.font,
            cursor: 'pointer'
          }}
        >
          Undo
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        style={{
          minHeight: 36,
          minWidth: 36,
          border: 'none',
          borderRadius: radius.control,
          background: 'transparent',
          color: colour.barInk,
          font: text.small.font,
          cursor: 'pointer'
        }}
      >
        ×
      </button>
    </div>
  );
}
